import React, { useCallback, useEffect } from "react";
import {
  Steps,
  Button,
  message,
  Descriptions,
  Badge,
  Space,
  Typography,
} from "antd";
import { connect } from "react-redux";

import QRCode from "qrcode.react";
import { Locale, ApiManager } from "../../utils";
import { getUserInfo } from "../../utils/redux/reducers/User";
import { getMerchantSettings } from "../../utils/redux/reducers/Merchant";

const i18n = Locale.getInstance();
const api = ApiManager.getInstance();
const { Title } = Typography;

const APPLYMENT_FINISH_STATES = new Set(["FINISH", "FINISHED"]);
const APPLYMENT_CANCELED_STATES = new Set(["CANCELED", "CANCELLED"]);

function isApplymentSucceededState(state) {
  return state != null && APPLYMENT_FINISH_STATES.has(state);
}

function allowsMultiPaymentNewApply(settings, primary) {
  if (!primary?.applyment_state) {
    return false;
  }
  if (!settings?.enable_multiple_payments) {
    return false;
  }
  return (
    APPLYMENT_FINISH_STATES.has(primary.applyment_state) ||
    APPLYMENT_CANCELED_STATES.has(primary.applyment_state)
  );
}

function normalizeApplymentResponse(payload) {
  if (payload == null) {
    return { primary: null, otherAccounts: [] };
  }
  if (
    Object.prototype.hasOwnProperty.call(payload, "applymentInfo") ||
    Object.prototype.hasOwnProperty.call(payload, "otherAccounts")
  ) {
    return {
      primary: payload.applymentInfo ?? null,
      otherAccounts: Array.isArray(payload.otherAccounts)
        ? payload.otherAccounts
        : [],
    };
  }
  return { primary: payload, otherAccounts: [] };
}

function getBadgeStatus(data) {
  if (!data?.applyment_state) {
    return "processing";
  }
  if (APPLYMENT_FINISH_STATES.has(data.applyment_state)) {
    return "success";
  }
  if (data.applyment_state === "REJECTED") {
    return "error";
  }
  return "processing";
}

function getStatusLabel(data) {
  if (!data?.applyment_state) {
    return "";
  }
  if (data.applyment_state === "CHECKING") {
    return i18n.t("applyment.checking");
  }
  if (data.applyment_state === "ACCOUNT_NEED_VERIFY") {
    return i18n.t("applyment.accountNeedVerify");
  }
  if (data.applyment_state === "AUDITING") {
    return i18n.t("applyment.auditing");
  }
  if (data.applyment_state === "REJECTED") {
    return i18n.t("applyment.rejected");
  }
  if (
    data.applyment_state === "CANCELED" ||
    data.applyment_state === "CANCELLED"
  ) {
    return i18n.t("applyment.canceled");
  }
  if (data.applyment_state === "NEED_SIGN") {
    return i18n.t("applyment.needSign");
  }
  if (APPLYMENT_FINISH_STATES.has(data.applyment_state)) {
    return i18n.t("applyment.finish");
  }
  if (data.applyment_state === "FROZEN") {
    return i18n.t("applyment.frozen");
  }
}

function ApplymentDetailDescriptions({ data, heading }) {
  if (!data || !data.applyment_state) {
    return null;
  }

  const titleEl = heading ? (
    <Title style={{ margin: "20px 20px 0 20px" }} level={4}>
      {heading}
    </Title>
  ) : (
    <Title style={{ margin: "20px 20px 0 20px" }} level={4}>
      {i18n.t("applyment.title")}
    </Title>
  );

  return (
    <div className="steps-info">
      <Descriptions
        style={{ backgroundColor: "#fff" }}
        column={{ xxl: 4, xl: 3, lg: 3, md: 3, sm: 2, xs: 1 }}
        title={titleEl}
        bordered
      >
        <Descriptions.Item label={i18n.t("applyment.id")}>
          {data.applyment_id}
        </Descriptions.Item>
        <Descriptions.Item label={i18n.t("applyment.status")}>
          <Badge status={getBadgeStatus(data)} text={getStatusLabel(data)} />
        </Descriptions.Item>
        <Descriptions.Item label={i18n.t("applyment.merchantId")}>
          {data.merchant_id}
        </Descriptions.Item>
        <Descriptions.Item label={i18n.t("applyment.createdDate")}>
          {data.created_at}
        </Descriptions.Item>
        <Descriptions.Item label={i18n.t("applyment.updatedDate")}>
          {data.updated_at}
        </Descriptions.Item>
        <Descriptions.Item label={i18n.t("applyment.subMerchantId")}>
          {data.sub_mchid}
        </Descriptions.Item>
      </Descriptions>
    </div>
  );
}

const steps = [
  {
    title: i18n.t("applyment.apply"),
    content: i18n.t("applyment.applyDescription"),
    error: "",
  },
  {
    title: i18n.t("applyment.procesing"),
    content: "",
    error: "",
  },
  {
    title: i18n.t("applyment.finish"),
    content: "",
    error: "",
  },
];

function ApplymentInfo({ user, settings }) {
  const [current, setCurrent] = React.useState(0);
  const [data, setData] = React.useState(null);
  const [otherAccounts, setOtherAccounts] = React.useState([]);
  const [error, setError] = React.useState(null);

  const applyPrimaryResponse = useCallback((primary) => {
    if (!primary) {
      setCurrent(0);
      setError(null);
      return;
    }
    if (primary.applyment_state === "REJECTED") {
      const { audit_detail } = primary;
      if (Array.isArray(audit_detail) && audit_detail.length > 0) {
        setError(audit_detail.map((i) => i.reject_reason));
      } else {
        setError(audit_detail);
      }
    } else {
      setError(null);
    }
    if (isApplymentSucceededState(primary.applyment_state)) {
      setCurrent(2);
    } else {
      setCurrent(1);
    }
  }, []);

  const getData = useCallback(async () => {
    const { token } = user;
    if (token) {
      try {
        const apiUrl = api.applyment.show;
        const apiConfig = {
          token,
        };
        const res = await apiUrl.run(apiConfig);
        if (res.result) {
          const { primary, otherAccounts: others } =
            normalizeApplymentResponse(res.data);
          setData(primary);
          setOtherAccounts(others);
          applyPrimaryResponse(primary);
        } else {
          message.warning(res.message);
        }
      } catch (error) {
        message.error(error.message);
      }
    }
  }, [user, applyPrimaryResponse]);

  const queryNow = useCallback(async () => {
    const { token } = user;
    if (token) {
      try {
        const apiUrl = api.applyment.query;
        const apiConfig = {
          token,
        };
        const res = await apiUrl.run(apiConfig);
        if (res.result) {
          const { primary, otherAccounts: others } =
            normalizeApplymentResponse(res.data);
          setData(primary);
          setOtherAccounts(others);
          applyPrimaryResponse(primary);
        } else {
          message.warning(res.message);
        }
      } catch (error) {
        message.error(error.message);
      }
    }
  }, [user, applyPrimaryResponse]);

  const applyNow = useCallback(async () => {
    setError(null);
    const { token } = user;
    if (token) {
      try {
        const apiUrl = api.applyment.apply;
        const apiConfig = {
          token,
        };
        const res = await apiUrl.run(apiConfig);
        if (res.result) {
          const { primary, otherAccounts: others } =
            normalizeApplymentResponse(res.data);
          setData(primary);
          setOtherAccounts(others);
          applyPrimaryResponse(primary);
        } else {
          message.warning(res.message);
        }
      } catch (error) {
        message.error(error.message);
      }
    }
  }, [user, applyPrimaryResponse]);

  useEffect(() => {
    getData();
  }, [getData]);

  const showMultiPaymentApply = allowsMultiPaymentNewApply(settings, data);

  const getStatus = (data) => {
    if (data && data.applyment_state) {
      if (isApplymentSucceededState(data.applyment_state)) {
        return "finish";
      }
      if (data.applyment_state === "REJECTED") {
        return "error";
      }
      return "";
    }
  };
  return (
    <>
      <Steps
        className="steps"
        current={current}
        status={getStatus(data)}
        items={steps.map((item) => ({ key: item.title, title: item.title }))}
      />
      <div className={`steps-content ${getStatus(data)} `}>
        {data && data.applyment_state === "NEED_SIGN" && (
          <div>
            <QRCode value={data.sign_url} />
            <div>{i18n.t("applyment.needSignHint")}</div>
          </div>
        )}

        {steps[current].content}
        <div>{error}</div>
      </div>
      <div className="steps-action">
        <Space direction="horizontal">
          {(current === 0 || showMultiPaymentApply) && (
            <Button type="primary" onClick={() => applyNow()}>
              {i18n.t("applyment.apply")}
            </Button>
          )}
          {current === 1 && (
            <Button type="primary" onClick={() => queryNow()}>
              {i18n.t("applyment.check")}
            </Button>
          )}
          {data &&
            (data.applyment_state === "REJECTED" ||
              (APPLYMENT_CANCELED_STATES.has(data.applyment_state) &&
                !settings?.enable_multiple_payments)) && (
              <Button type="primary" onClick={() => applyNow()}>
                {i18n.t("applyment.applyAgain")}
              </Button>
            )}
        </Space>
      </div>
      <ApplymentDetailDescriptions data={data} />
      {otherAccounts.map((acc, index) => (
        <ApplymentDetailDescriptions
          key={acc.applyment_id ?? acc.sub_mchid ?? `other-${index}`}
          data={acc}
          heading={`${i18n.t("applyment.otherAccountsHeading")} (${
            index + 1
          })`}
        />
      ))}
    </>
  );
}

export default connect((state) => ({
  user: getUserInfo(state),
  settings: getMerchantSettings(state),
}))(ApplymentInfo);
